import { Link as RouterLink } from 'react-router-dom';
// @mui
import { Box, Link, ListItemText, Tooltip, useTheme } from '@mui/material';
import { keyframes } from '@mui/system';
// locales
import { useLocales } from '../../../locales';
// auth
import RoleBasedGuard from '../../../auth/RoleBasedGuard';
//
import Iconify from '../../iconify';
import Label from '../../label';
//
import { NavItemProps } from '../types';
import { StyledIcon, StyledItem } from './styles';

// ----------------------------------------------------------------------

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.06); }
  100% { transform: scale(1); }
`;

export default function NavItem({
  item,
  depth,
  open,
  active,
  isExternalLink,
  ...other
}: NavItemProps) {
  const { translate } = useLocales();
  const theme = useTheme();
  const { title, path, icon, info, children, disabled, caption, roles, label } =
    item;

  // Palette for the rounded label badge (falls back to grey for 'default').
  const labelPalette =
    label && label.color !== 'default'
      ? theme.palette[label.color]
      : {
          main: theme.palette.grey[500],
          contrastText: theme.palette.common.white,
        };

  const renderContent = (
    <StyledItem
      depth={depth}
      active={active}
      disabled={disabled}
      caption={!!caption}
      {...other}
    >
      {icon && (
        <StyledIcon active={active} depth={depth}>
          {icon}
        </StyledIcon>
      )}

      <ListItemText
        primary={`${translate(title)}`}
        secondary={
          caption && (
            <Tooltip title={`${translate(caption)}`} placement="top-start">
              <span>{`${translate(caption)}`}</span>
            </Tooltip>
          )
        }
        primaryTypographyProps={{
          noWrap: true,
          component: 'span',
          variant: active ? 'subtitle2' : 'body2',
          color: active ? theme.palette.primary.main : 'inherit',
        }}
        secondaryTypographyProps={{
          noWrap: true,
          variant: 'caption',
        }}
      />

      {label &&
        (label.icon ? (
          <Tooltip title={`${translate(label.text)}`} placement="top">
            <Box
              sx={{
                ml: 1,
                flexShrink: 0,
                width: 26,
                height: 26,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: labelPalette.main,
                color: labelPalette.contrastText,
                willChange: 'transform',
                transformOrigin: 'center',
                ...(label.animated && {
                  animation: `${pulse} 1.8s ease-in-out infinite`,
                  '@media (prefers-reduced-motion: reduce)': {
                    animation: 'none',
                  },
                }),
              }}
            >
              <Iconify icon={label.icon} width={16} />
            </Box>
          </Tooltip>
        ) : (
          <Label
            color={label.color}
            sx={{
              marginLeft: theme.spacing(1),
              backgroundColor: theme.palette.error.main,
              color: theme.palette.error.contrastText,
              willChange: 'transform',
              transformOrigin: 'center',
              ...(label.animated && {
                animation: `${pulse} 1.8s ease-in-out infinite`,
                '@media (prefers-reduced-motion: reduce)': {
                  animation: 'none',
                },
              }),
            }}
          >
            {translate(label.text)}
          </Label>
        ))}

      {info && (
        <Box component="span" sx={{ lineHeight: 0 }}>
          {info}
        </Box>
      )}

      {!!children && (
        <Iconify
          width={16}
          icon={
            open ? 'eva:arrow-ios-downward-fill' : 'eva:arrow-ios-forward-fill'
          }
          sx={{ ml: 1, flexShrink: 0 }}
        />
      )}
    </StyledItem>
  );

  const renderItem = () => {
    // ExternalLink
    if (isExternalLink)
      return (
        <Link href={path} target="_blank" rel="noopener" underline="none">
          {renderContent}
        </Link>
      );

    // Has child
    if (children) {
      return renderContent;
    }

    // Default
    return (
      <Link component={RouterLink} to={path} underline="none">
        {renderContent}
      </Link>
    );
  };

  return <RoleBasedGuard roles={roles}> {renderItem()} </RoleBasedGuard>;
}
